export const SOLUTIONS = [
  {
    slug: "capture",
    name: "Data Collection",
    heading: "Turn real-world demonstrations into usable data.",
    intro:
      "Build task-specific collection protocols, quality thresholds and delivery formats for first-person demonstrations, contact and motion signals. Equip your own team or organize collection and dataset delivery as a project.",
    audience: "Human demonstrations, enterprise data delivery and proprietary datasets",
    image: "/assets/catalog/fmg-hero-cutout.webp",
    imageNote:
      "Concept illustration of combined Ego and forearm-band capture; configuration selected per task.",
    outcome: "Collection protocol, paired data and batch quality reports",
    products: ["thor-duo", "thor-quad", "thor-hex", "umi", "data-glove", "fmg-imu"],
    scenarios: [
      "First-person manipulation datasets",
      "Bimanual and tactile demonstrations",
      "Expanded coverage across tasks, environments and participants",
    ],
    baseline: [
      "Select Thor-Duo / Quad / Hex for the field of view and synchronization requirements.",
      "Choose UMI or tactile gloves for the manipulation task.",
      "Size the collection workstation, storage and calibration tools for throughput.",
    ],
    optional: [
      "FMG + IMU research with independently measured contribution.",
      "Multiple collection sites, human review and data processing.",
      "Adapters for customer-owned devices and data formats.",
    ],
    deliverables: [
      [
        "Collection protocol",
        "Task definitions, action segments, scene variables, device and calibration versions, and failure-sample rules.",
      ],
      [
        "Data package",
        "Raw recordings, timestamps, calibration and task metadata; annotations and estimates stored separately.",
      ],
      [
        "Quality report",
        "Completeness, dropped frames, timing deviations, coverage and sample-review findings for each batch.",
      ],
      [
        "Delivery manifest",
        "Versions, file lists, checksums, field dictionary, usage rights and rejected-sample handling.",
      ],
    ],
    acceptance:
      "Approve a pilot batch before scaling. Measure accepted episodes, usable duration or completed-task samples; total recording hours are not automatically training-ready data.",
    boundary:
      "Human-side observations are not robot action labels. Trajectory estimation, force mapping and transfer between embodiments require additional calibration and validation.",
  },
  {
    slug: "workstation",
    name: "Robot Learning Workstations",
    heading: "Build a repeatable experiment around one task.",
    intro:
      "Combine robots, teleoperation, vision and compute for research teams. Connect demonstrations, baseline training and physical evaluation in a reproducible experiment with a defined task and acceptance criteria.",
    audience: "University laboratories, enterprise R&D teams and robotics developers",
    image: "/assets/catalog/atlas-d-actrium-silver.webp",
    imageNote: "Workstation concept illustration; not a delivery photograph.",
    outcome: "Integrated hardware, a task baseline and physical evaluation",
    products: ["research-arm", "atlas-a7", "atlas-d", "atlas-m", "atlas-s", "atlas-r1"],
    scenarios: [
      "Single-arm grasping and manipulation",
      "Bimanual collaboration and teleoperation",
      "Mobile manipulation across workspaces",
    ],
    baseline: [
      "Select Atlas A6 / A7 / D / M / S for degrees of freedom, workspace and task.",
      "Specify cameras, end effectors, fixtures and safety equipment.",
      "Define collection and inference compute, software environment and experiment records.",
    ],
    optional: [
      "Tactile end effectors and paired human-side capture.",
      "GPU compute scoped per project (our own U.S. GPU data center is under construction) and model adaptation services.",
      "Atlas R1 open-frame research concept assessment.",
    ],
    deliverables: [
      [
        "Integration manifest",
        "Robot, camera, end effector, compute and software versions, with interfaces and responsibility boundaries.",
      ],
      [
        "Task samples",
        "Teleoperation procedure, recorded fields, calibration and demonstrations within the agreed scope.",
      ],
      [
        "Reproducible baseline",
        "Training configuration, data version, runtime, weights and licenses when included in the project.",
      ],
      [
        "Evaluation records",
        "Fixed conditions, success rate, task duration, interventions and failure categories with raw logs.",
      ],
    ],
    acceptance:
      "Accept hardware, safety functions, recording, model reproducibility and physical task results separately. Success rates must name the task, objects, starting states and trial count.",
    boundary:
      "A GPU option does not include training services. Model adaptation, optimization and evaluation need explicit scope. High-power GPUs may require an external workstation.",
  },
  {
    slug: "multimodal",
    name: "Multimodal Research",
    heading: "Measure what each signal adds.",
    intro:
      "Bring vision, tactile sensing, hand pose, FMG + IMU and robot state into a shared experiment. Align timing and data versions, then use controlled comparisons to test whether added signals improve manipulation.",
    audience: "Multimodal learning, dexterous manipulation and joint data-model research",
    image: "/assets/catalog/tactile-glove-samples.webp",
    imageNote: "Tactile glove samples; accessories and quantities depend on configuration.",
    outcome: "Paired modalities, controlled experiments and targeted recollection",
    products: ["thor-quad", "thor-hex", "umi", "data-glove", "fmg-imu", "atlas-d"],
    scenarios: [
      "Vision-tactile manipulation learning",
      "Human demonstration to robot action mapping",
      "Cross-user FMG, IMU and vision experiments",
    ],
    baseline: [
      "Choose modalities for a specific research hypothesis.",
      "Define clock alignment, coordinate calibration and stream association.",
      "Maintain data versions and common training and evaluation splits.",
    ],
    optional: [
      "Independent force or pose reference sensors.",
      "Robot logs, failure episodes and corrective demonstrations.",
      "Third-party model APIs, offline algorithms and private compute integration.",
    ],
    deliverables: [
      [
        "Synchronization package",
        "Device clocks, rates, coordinate frames, extrinsics, alignment method and measured errors.",
      ],
      [
        "Layered dataset",
        "Raw observations, algorithm estimates, human annotations and robot action labels kept distinct.",
      ],
      [
        "Ablation studies",
        "Compare vision-only, individual added signals and combined inputs on the same task and split.",
      ],
      [
        "Research report",
        "Benefits, failure conditions and performance across participants, dates and scenes, with the next collection requirements.",
      ],
    ],
    acceptance:
      "Check timing and completeness before offline metrics. Claims about better robot performance require controlled physical tests; gesture-recognition scores are not task success rates.",
    boundary:
      "FMG records pressure patterns associated with muscle deformation. It is not EMG or direct contact-force measurement. The benefit of an added modality is a hypothesis to test.",
  },
];

export const DATA_WORKFLOW_STEPS = [
  ["Connect and synchronize", "Device setup, raw records, clocks and calibration."],
  ["Process and review", "Cleaning, segmentation, annotation and exception review."],
  ["Release a dataset", "Versions, permissions, evaluation splits and export manifests."],
  ["Train and evaluate", "Environments, weights, metrics and physical robot logs."],
  ["Learn from failures", "Identify missing scenarios and collect corrective demonstrations."],
];
